// CORE-847 §2 / §6 / §7 — the collapsed launcher.
//
// Idle is the avatar and nothing else: the «Agapella AI / Ваш помощник» plate
// used to hold ~172 px of screen at all times, and the spec's first line is to
// stop that. The caption now slides out on hover or keyboard focus and slides
// back away on leave.
//
// Two rules the old launcher broke and this one keeps:
//   · the avatar is never replaced by a big square × — it stays the avatar and
//     acts as a toggle, closing lives on the pop-up's own header button (§7);
//   · the widget never claims layout — it is an overlay, positioned by the
//     caller, so unfolding the caption cannot shift the page under it (§6).
//
// Motion is CSS transitions, not `Animated`. Two reasons, both learned here:
// the widget is a web surface (native never renders it — see widgetVisibility),
// and an Animated timing on this tree read as an instant swap in the browser
// while a transition is exactly the «opacity + transform, ease-out» the spec
// asks for. It is also the repo's existing idiom for web hover states
// (BecomeMasterModal, AgapellaChannelPreview). The native pass will swap these
// for Animated/Reanimated — the styles are plain RN objects, so only the
// transition line goes.
import React, { useEffect, useRef, useState } from 'react';
import { Animated, Image, Platform, Pressable, StyleSheet, Text, View, type GestureResponderHandlers } from 'react-native';

import { AGAPELLA_AI_ICON } from '../constants/aiCompanions';
import { useTheme } from '../context/ThemeContext';
import { useReduceMotion } from '../hooks/useReduceMotion';
import { LauncherAura } from './LauncherAura';
import { usePageAway } from './usePageAway';
import { AVATAR_SIZE, RING_WIDTH, type Point } from './widgetPosition';
import { typographyTokens } from '../styles/typographyTokens';

const IS_WEB = Platform.OS === 'web';

/** Plate travel, the same in both directions so it drifts out and back at one
 *  pace. (The note that used to sit here still claimed the owner's earlier
 *  0.7 s round; the value has been 200 ms since the last tuning pass.) */
const CAPTION_MS = 200;
/** How far the plate travels out from under the portrait while it fades in. */
const CAPTION_TRAVEL = 50;
/** The button's own reaction stays quick: a control should answer at once even
 *  when the label beside it takes its time. */
const BUTTON_MS = 240;
/**
 * The copy rides its own `translateY` on top of the plate's travel: it starts
 * low inside the plate and settles to `translateY(0)`, then drops back on the
 * way out — the label arrives rather than being carried in.
 */
const CAPTION_TEXT_RISE = 30;
const CAPTION_TEXT_MS = 300;
const CAPTION_TEXT_OUT_MS = 500;
/** The copy follows the plate rather than racing it. */
const CAPTION_TEXT_DELAY_MS = 220;
const CAPTION_WIDTH = 196;
const CAPTION_HEIGHT = 50;
/** How far the plate tucks under the portrait, so the two read as one object. */
const CAPTION_TUCK = AVATAR_SIZE / 2;
/** Spec §1: «a subtle hover scale effect» — subtle being the operative word. */
const HOVER_SCALE = 1.06;

interface Props {
  anchor: Point;
  /** Which way the caption unfolds — away from the nearest viewport edge. */
  side: 'left' | 'right';
  /** Chat pop-up is up: the launcher stays an avatar and reads as a toggle. */
  open: boolean;
  dragging: boolean;
  unread: number;
  /** Stored position has been read; until then the launcher is invisible so it
   *  never flashes in the default corner before jumping to where it was left. */
  ready: boolean;
  onPress: () => void;
  panHandlers: GestureResponderHandlers;
}

export const AgapellaLauncher: React.FC<Props> = ({
  anchor, side, open, dragging, unread, ready, onPress, panHandlers,
}) => {
  const { colors, isDark } = useTheme();
  const reduceMotion = useReduceMotion();

  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  /**
   * QA 25.08.2026 (Вероника) — «в idle видны обе подписи, активный элемент body»
   * and «после закрытия по иконке подпись больше не убирается при уходе курсора».
   * One defect, and this ref is the fix.
   *
   * A click leaves the browser's focus ON the button (verified: after any press
   * `document.activeElement` is the launcher). `focused` therefore stayed true
   * for the rest of the session, hover-out cleared only `hovered`, and the
   * caption never folded back — which is exactly the «idle with the plate open»
   * the report describes.
   *
   * The caption is a hover / keyboard affordance, so it must answer the same
   * question CSS `:focus-visible` answers: was this focus asked for, or merely
   * left behind by a pointer? Which is why {@link isKeyboardFocus} below ASKS
   * the browser rather than guessing — it is the only thing that knows, and
   * guessing is what let the owner's second report through (open by icon, close
   * by icon, switch tabs, come back → the plate unfolded, because returning to a
   * tab restores the focus the click had left on the button and a heuristic read
   * that restoration as a fresh Tab-in).
   *
   * The ref stays as the fallback for anything without `:focus-visible` support:
   * a press arms it and `onPressOut` disarms it — Pressable fires that on a
   * drag-terminated gesture too, so it cannot stay armed and swallow a later
   * Tab-in. `setFocused(false)` on press-in covers the same ground from the
   * other side, since react-native-web delivers `focus` BEFORE `onPressIn`.
   */
  const pointerFocus = useRef(false);
  /** The host node, purely to ask it `:focus-visible` — see above. */
  const hostRef = useRef<unknown>(null);

  /**
   * Whether the focus now on the launcher is one the user asked for.
   *
   * `:focus-visible` is exactly this question, and the browser answers it with
   * everything we cannot see: how the focus was acquired, and whether it is
   * merely being restored to where it already was. Falls back to the pointer
   * flag where the selector is unavailable (older engines, native).
   */
  const isKeyboardFocus = () => {
    const node = hostRef.current as { matches?: (selector: string) => boolean } | null;
    if (!node || typeof node.matches !== 'function') return !pointerFocus.current;
    try {
      return node.matches(':focus-visible');
    } catch {
      return !pointerFocus.current;
    }
  };
  // A press or a drag mutes the caption until the NEXT hover-in. Both end with
  // the pointer standing on the avatar while every hover condition holds —
  // after closing the chat via the icon, and after dropping a carried icon —
  // and in both cases the label unfolding at that moment read as the widget
  // talking over what the user just did.
  //
  // The mute clears on re-entry, not on leave: a click parks residual focus on
  // the Pressable, so clearing on hover-out let `focused` unfold the label the
  // instant the cursor walked away. Cleared on blur too, so keyboard users get
  // it back on the next Tab-in.
  const [muted, setMuted] = useState(false);

  // Arm it the moment a drag starts; it survives the release and clears only
  // on re-entry, so the label returns on the next hover, not on drop.
  useEffect(() => { if (dragging) setMuted(true); }, [dragging]);

  // Leaving the page for another tab or window ends the hover as far as the user
  // is concerned — but not as far as the DOM is concerned, which sends no
  // `mouseleave` to a page nobody is looking at. Without this the plate is still
  // unfolded on return, with the cursor long gone elsewhere. `muted` is left
  // alone: the next hover-in lifts it, exactly as after any other press.
  usePageAway(() => { setHovered(false); setFocused(false); });

  // Hidden while the chat is up (the pop-up header already names her), while
  // she is being carried — dragging is about placing the icon, not reading
  // about it — and right after a press or a drop (see above).
  const expanded = (hovered || focused) && !open && !dragging && !muted;

  // `prefers-reduced-motion` gates only the infinite idle loops (LauncherAura's
  // glow / ring / sparks). The interactive transitions below run for everyone —
  // gating them turned out to be why «нет плавности» kept being reported: with
  // Windows' «Эффекты анимации» off the browser reports reduced motion, and
  // every tuned transition here was being swapped for an instant cut. A one-off,
  // user-initiated fade is not the motion that setting exists to remove.
  const ease = (props: string) => (IS_WEB ? ({ transition: props } as any) : null);
  const textMs = expanded ? CAPTION_TEXT_MS : CAPTION_TEXT_OUT_MS;
  const textDelay = expanded ? CAPTION_TEXT_DELAY_MS : 0;

  const label = open
    ? 'Свернуть Агапеллу'
    : unread > 0
      ? `Открыть Агапеллу, непрочитанных сообщений: ${unread}`
      : 'Открыть Агапеллу';

  // Rendered position. A released icon GLIDES to wherever the drop resolved to:
  // native takes its quadrant's corner, and on web an open chat window sends it
  // to the nearest dock. Either way `anchor` changes in one jump, and springing
  // the rendered position to it is what turns «резко» into a settle.
  //
  // Not animated: the drag itself (the value is written through so the icon
  // stays glued to the pointer), and the very first ready-anchor — the restored
  // position — which would otherwise parade the icon across the screen at every
  // app start. A plain web free-drag lands exactly where it was let go, so its
  // spring has nothing to travel and is invisible either way.
  const pos = useRef(new Animated.ValueXY({ x: anchor.x, y: anchor.y })).current;
  const wasReadyRef = useRef(false);
  useEffect(() => {
    const target = { x: anchor.x, y: anchor.y };
    const justReady = ready && !wasReadyRef.current;
    wasReadyRef.current = ready;
    // CORE-731, QA FAIL 22.09.2026 — a hidden tab never fires animation frames,
    // and the web spring runs on them: `anchor` had already moved off «Войти как
    // ведущий», but the rendered icon (and its hit area) stayed parked on the
    // button until the tab came to the front. Nobody can watch a glide in a
    // background tab, so there the icon is placed at once.
    const pageHidden = IS_WEB && typeof document !== 'undefined' && document.visibilityState === 'hidden';
    if (dragging || !ready || justReady || pageHidden) {
      pos.setValue(target);
      return;
    }
    const glide = Animated.spring(pos, {
      toValue: target,
      friction: 8,
      tension: 70,
      // No native animated module on web — asking for the native driver there
      // only earns a console warning and falls back to this same JS driver.
      useNativeDriver: !IS_WEB,
    });
    glide.start();
    return () => glide.stop();
  }, [anchor.x, anchor.y, dragging, ready, pos]);

  return (
    <Animated.View
      style={[styles.root, { transform: pos.getTranslateTransform(), opacity: ready ? 1 : 0 }]}
      {...panHandlers}
    >
      {/* §6 — the label must never move the button: it is absolutely positioned
          beside the avatar and takes no pointer events, so neither its arrival
          nor its width can reach the pressable.

          Web only — the caption is a hover affordance, and native has no hover:
          there the widget is just the floating icon (CORE-847, native pass). */}
      {IS_WEB && <View
        pointerEvents="none"
        testID="agapella-widget-caption"
        style={[
          styles.caption,
          side === 'left' ? styles.captionLeft : styles.captionRight,
          {
            backgroundColor: isDark ? 'rgba(24, 21, 26, 0.94)' : 'rgba(255, 255, 255, 0.96)',
            // Warm hairline instead of the neutral divider colour: it is the
            // same glow that rings the portrait, carried around the plate.
            borderColor: isDark ? 'rgba(233, 130, 69, 0.5)' : 'rgba(233, 130, 69, 0.34)',
            // Lifts the plate off the page; warm-tinted on light so the shadow
            // reads as her glow spilling over, not as a grey smudge.
            ...(IS_WEB
              ? ({
                boxShadow: isDark
                  ? '0 12px 30px rgba(0, 0, 0, 0.45)'
                  : '0 10px 26px rgba(171, 105, 54, 0.30)',
              } as any)
              : null),
            opacity: expanded ? 1 : 0,
            // Folded, the plate waits closer to the portrait; expanding drifts
            // it out to its resting place while it fades in.
            transform: [{ translateX: expanded ? 0 : side === 'left' ? CAPTION_TRAVEL : -CAPTION_TRAVEL }],
          },
          ease(`opacity ${CAPTION_MS}ms ease-in-out, transform ${CAPTION_MS}ms ease-in-out`),
        ]}
      >
        <View
          style={[
            styles.captionText,
            {
              opacity: expanded ? 1 : 0,
              transform: [{ translateY: expanded ? 0 : CAPTION_TEXT_RISE }],
            },
            ease(
              `opacity ${textMs}ms ease-in-out ${textDelay}ms, transform ${textMs}ms ease-in-out ${textDelay}ms`,
            ),
          ]}
        >
          <Text numberOfLines={1} style={[styles.title, { color: colors.textPrimary }]}>Agapella AI</Text>
          <View style={styles.statusRow}>
            <Text numberOfLines={1} style={[styles.subtitle, { color: colors.textMuted }]}>Ваш помощник</Text>
            <View style={styles.onlineDot} />
          </View>
        </View>
      </View>}

      <Pressable
        ref={(node) => { hostRef.current = node; }}
        onPress={() => { setMuted(true); onPress(); }}
        // The press drops the caption's focus claim — see the ref's note above.
        onPressIn={() => { pointerFocus.current = true; setFocused(false); }}
        onPressOut={() => { pointerFocus.current = false; }}
        // Re-entry lifts the mute — except the transient hover-in that fires
        // mid-drag while the icon chases the pointer.
        onHoverIn={IS_WEB ? () => { setHovered(true); if (!dragging) setMuted(false); } : undefined}
        onHoverOut={IS_WEB ? () => setHovered(false) : undefined}
        // Keyboard focus opens the caption; the residue of a click does not —
        // and neither does that residue being handed back when the user returns
        // to the tab.
        onFocus={() => { if (isKeyboardFocus()) setFocused(true); }}
        onBlur={() => { setFocused(false); if (!dragging) setMuted(false); }}
        accessibilityRole="button"
        accessibilityLabel={label}
        testID="agapella-widget-launcher"
        style={[styles.hit, IS_WEB && ({ cursor: dragging ? 'grabbing' : 'grab' } as any)]}
      >
        {/* The hover effect lives HERE, inside the hit area, never on it. An
            earlier version lifted the pressable itself: that moved it out from
            under a cursor resting on its bottom edge, hover dropped, the widget
            fell back, hover returned — a visible jitter. Scaling from the centre
            only ever grows the box, so the pointer stays inside it. */}
        <View
          style={[
            styles.visual,
            {
              backgroundColor: isDark ? 'rgba(43, 38, 35, 0.94)' : '#FFFFFF',
              transform: [{ scale: expanded ? HOVER_SCALE : 1 }],
            },
            ease(`transform ${BUTTON_MS}ms ease-out`),
          ]}
        >
          <LauncherAura size={AVATAR_SIZE} active={expanded || open} isDark={isDark} reduceMotion={reduceMotion} />
          <Image source={AGAPELLA_AI_ICON} style={styles.avatar} />
        </View>
        {unread > 0 && (
          <View style={[styles.badge, { borderColor: colors.pageBg }]} testID="agapella-widget-unread">
            <Text style={styles.badgeText}>{unread > 99 ? '99+' : unread}</Text>
          </View>
        )}
      </Pressable>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  root: {
    position: 'absolute',
    // Placed by the animated translate above, not by left/top — a transform can
    // ride the native driver, so the corner glide never touches layout.
    left: 0,
    top: 0,
    width: AVATAR_SIZE,
    height: AVATAR_SIZE,
    // The glow and the caption both reach outside this box on purpose.
    ...(IS_WEB ? ({ touchAction: 'none' } as any) : null),
  },
  hit: {
    width: AVATAR_SIZE,
    height: AVATAR_SIZE,
    borderRadius: AVATAR_SIZE / 2,
  },
  visual: {
    ...StyleSheet.absoluteFillObject,
    borderRadius: AVATAR_SIZE / 2,
  },
  avatar: {
    position: 'absolute',
    top: RING_WIDTH,
    left: RING_WIDTH,
    width: AVATAR_SIZE - RING_WIDTH * 2,
    height: AVATAR_SIZE - RING_WIDTH * 2,
    borderRadius: (AVATAR_SIZE - RING_WIDTH * 2) / 2,
  },
  caption: {
    position: 'absolute',
    top: (AVATAR_SIZE - CAPTION_HEIGHT) / 2,
    width: CAPTION_WIDTH,
    height: CAPTION_HEIGHT,
    borderRadius: CAPTION_HEIGHT / 2,
    borderWidth: 1,
    justifyContent: 'center',
    gap: 2,
    // The copy's 40 px rise starts below the pill's bottom edge — the plate
    // masks it, so the text surfaces INTO the plate instead of hanging under it.
    overflow: 'hidden',
    ...(IS_WEB ? ({ backdropFilter: 'blur(14px)' } as any) : null),
  },
  // Tucked half a portrait under the avatar and padded there by the same, so the
  // text starts beside her rather than behind her.
  captionRight: {
    left: CAPTION_TUCK,
    paddingLeft: CAPTION_TUCK + 16,
    paddingRight: 16,
  },
  captionLeft: {
    right: CAPTION_TUCK,
    paddingRight: CAPTION_TUCK + 16,
    paddingLeft: 16,
  },
  captionText: { gap: 2 },
  title: { ...typographyTokens.bodyAccent },
  statusRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  subtitle: { ...typographyTokens.label },
  onlineDot: { width: 7, height: 7, borderRadius: 4, backgroundColor: '#22C55E' },
  badge: {
    position: 'absolute',
    top: -2,
    right: -2,
    minWidth: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 2,
    backgroundColor: '#EF4444',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 5,
  },
  badgeText: { ...typographyTokens.captionAccent, color: '#fff' },
});
